/**
 * OALogoVersionsApi - Finds the other branding version of a product.
 *
 * Some products exist twice while the smaller logo rolls out, both tagged with
 * window.oaLogoVersionsTag. This resolves the sibling of the product on screen and says
 * which of the two is the original logo and which the updated one, so
 * pdp-logo-versions.js can offer the one that still has the size.
 *
 * The pair is NOT identifiable by handle or by an equal title. Live example:
 *
 *   updated   "Oner Seamless Fitted Tank Top New Branding | Black"
 *             oner-seamless-fitted-tank-top-new-branding-black   created 2026-07-28
 *   original  "Oner Seamless Fitted Tank Top | Black"
 *             oner-seamless-fitted-vest-black                    created 2026-05-20
 *
 * The handles have nothing in common, and the titles differ by the branding marker. What
 * does hold is that stripping that marker from the base title leaves the two identical,
 * and the colour half is the same on both — so that is what the matching is built on. The
 * "-2" handle convention is kept only as a last-resort fallback for pairs set up that way.
 *
 * Depends on OAStorefrontApi being loaded first.
 */
window.OALogoVersionsApi = {
  // handle -> { at, value }. Always go through cacheGet / cacheSet: entries expire.
  cache: new Map(),

  // Availability is what this is fetched for, so an entry held for the life of the page
  // would keep offering a size the sibling sold out ten minutes ago. Matches the TTL in
  // oa-products-api.js and pdp-color-switch.js.
  CACHE_TTL_MS: 5 * 60 * 1000,

  // A shopper works through a colour rail, and every colour is its own pair of handles.
  // Well above one visit, low enough to stay bounded.
  CACHE_MAX: 40,

  // handle -> the request that will cache it. Written only on resolve, so without this a
  // pointer sweeping the rail fires a second document for a handle already in flight.
  inflight: {},

  // Phrases that mark which branding a title belongs to. Order matters only in that the
  // longer forms are listed first, so "new branding" is consumed before "new logo" could
  // partially match something else. Matched case-insensitively, on the base title.
  UPDATED_MARKERS: ['new branding', 'updated branding', 'updated logo', 'new logo'],
  // 'with grey logo' names the original branding in the live catalogue, where
  // 'original logo' never actually appears. Classifying it as ORIGINAL rather than as noise
  // is what stops `null` meaning two different things — "this is the grey-logo version" and
  // "this title says nothing about logos" — which let an unmarked colour of the same
  // garment into the grey-logo family.
  // 'with grey logo' names the original branding in the live catalogue, where
  // 'original logo' never actually appears. 'with white logo' is here too, so every phrase
  // that can appear in a title carries a role and there is no third list.
  //
  // Consequence worth knowing: white-logo and grey-logo products are therefore the same
  // branding version as far as the title matching is concerned, so they share one colour
  // family. Where that is wrong for a specific pair, list it in oa-logo-versions-map.js —
  // the map bypasses titles entirely.
  //
  // Every phrase in both lists is stripped from the base title AND from the search prefix,
  // which is what lets a grey-logo PDP search "… Bra*" and match new-branding titles;
  // leaving any of them in would search "… Bra with Grey Logo*" and find only its own family.
  ORIGINAL_MARKERS: [
    'original branding', 'original logo', 'with grey logo', 'with gray logo', 'with white logo'
  ],

  // Returns undefined for a miss, so a cached "this product has no sibling" (null) is
  // told apart from "never looked" and is not re-fetched on every size click.
  cacheGet: function(key) {
    var entry = this.cache.get(key);
    if (!entry) return undefined;
    if (Date.now() - entry.at >= this.CACHE_TTL_MS) {
      this.cache.delete(key);
      return undefined;
    }
    return entry.value;
  },

  cacheSet: function(key, value) {
    this.cache.set(key, { at: Date.now(), value: value });
    if (this.cache.size > this.CACHE_MAX) this.prune();
  },

  // Drop what has expired, then the oldest of what is left — a Map iterates in insertion
  // order, so its first keys are the least recently written.
  prune: function() {
    var self = this;
    var now = Date.now();
    this.cache.forEach(function(entry, key) {
      if (now - entry.at >= self.CACHE_TTL_MS) self.cache.delete(key);
    });
    if (this.cache.size <= this.CACHE_MAX) return;
    var excess = this.cache.size - this.CACHE_MAX;
    var keys = this.cache.keys();
    for (var i = 0; i < excess; i++) this.cache.delete(keys.next().value);
  },

  /**
   * Split a product title into the parts the pairing is matched on.
   *
   * "Oner Seamless Fitted Tank Top New Branding | Black"
   *   -> { base: 'oner seamless fitted tank top', colour: 'black', marker: 'updated' }
   *
   * @param {string} title
   * @returns {{base: string, colour: string, marker: string|null, rawBase: string}}
   */
  parseTitle: function(title) {
    var parts = String(title || '').split('|');
    var rawBase = (parts[0] || '').replace(/™/g, ' ').trim();
    var colour = (parts[1] || '').trim().toLowerCase();
    var base = rawBase.toLowerCase();
    var marker = null;

    this.UPDATED_MARKERS.forEach(function(phrase) {
      if (base.indexOf(phrase) !== -1) {
        if (!marker) marker = 'updated';
        base = base.split(phrase).join(' ');
      }
    });
    this.ORIGINAL_MARKERS.forEach(function(phrase) {
      if (base.indexOf(phrase) !== -1) {
        if (!marker) marker = 'original';
        base = base.split(phrase).join(' ');
      }
    });
    return {
      base: base.replace(/\s+/g, ' ').trim(),
      colour: colour.replace(/\s+/g, ' ').trim(),
      marker: marker,
      rawBase: rawBase
    };
  },

  // Two products are the same garment in the same colour when their marker-stripped base
  // titles are equal and their colour halves are equal. Equality, not a prefix test — a
  // prefix would pair "… Tank Top" with "… Tank Top Long".
  isPair: function(a, b) {
    if (!a.base || !b.base) return false;
    return a.base === b.base && a.colour === b.colour;
  },

  // createdAt is what separates the original logo from the updated one when the titles
  // carry no marker, and it appears in no other query in this theme — OAProductsApi does
  // not select it and its transform has no date field. So this file carries its own field
  // set rather than reusing that one.
  productFields: `
    id
    handle
    title
    tags
    createdAt
    availableForSale
    featuredImage { url }
    priceRange { minVariantPrice { amount } }
    compareAtPriceRange { minVariantPrice { amount } }
    variants(first: 100) {
      nodes {
        id
        sku
        title
        availableForSale
        price { amount }
        compareAtPrice { amount }
        selectedOptions { name value }
      }
    }`,

  // Step 1: find candidates, ids and titles only. Deliberately light — every colour of
  // both branding versions comes back, so a run of 40+ products is normal, and asking for
  // variants here would put the document over the Storefront complexity budget.
  // tag: and title: are the filters proven to work on this store (oa-color-variants-api.js
  // searches on exactly this shape); handle: is silently ignored here.
  searchQuery: `query findLogoVersionCandidates($searchQuery: String!) {
    products(first: 100, query: $searchQuery) {
      nodes {
        id
        handle
        title
        createdAt
        tags
      }
    }
  }`,

  // Step 2: the full record, for the two products that survived matching.
  detailsQuery: function() {
    return 'query getLogoVersionDetails($ids: [ID!]!) {\n' +
      '  nodes(ids: $ids) {\n    ... on Product {' + this.productFields + '\n    }\n  }\n}';
  },

  // Exact lookups, one aliased product(handle:) per handle. The fallback for when the
  // title match finds nothing, and the only place the "-2" naming is relied on. Aliases
  // are positional: handles contain hyphens and cannot be GraphQL aliases.
  buildHandleQuery: function(handles) {
    var fields = this.productFields;
    var selections = handles.map(function(h, i) {
      return '\n  p' + i + ': product(handle: ' + JSON.stringify(h) + ') {' + fields + '\n  }';
    }).join('');
    return 'query getLogoVersionsByHandles {' + selections + '\n}';
  },

  /**
   * Shape a GraphQL product into what pdp-logo-versions.js renders. Prices are integer
   * minor units, matching OAProductsApi and what Shopify.formatMoney expects.
   */
  transform: function(gql) {
    if (!gql) return null;
    var compareAt = gql.compareAtPriceRange && gql.compareAtPriceRange.minVariantPrice
      ? Math.round(parseFloat(gql.compareAtPriceRange.minVariantPrice.amount) * 100)
      : null;

    return {
      id: parseInt(OAStorefrontApi.toNumericId(gql.id), 10),
      handle: gql.handle,
      title: gql.title,
      tags: gql.tags || [],
      createdAt: gql.createdAt,
      available: gql.availableForSale,
      image: gql.featuredImage ? gql.featuredImage.url : null,
      price: Math.round(parseFloat(gql.priceRange.minVariantPrice.amount) * 100),
      compare_at_price: compareAt,
      variants: (gql.variants.nodes || []).map(function(v) {
        return {
          id: parseInt(OAStorefrontApi.toNumericId(v.id), 10),
          sku: v.sku,
          title: v.title,
          option1: v.selectedOptions[0] ? v.selectedOptions[0].value : null,
          option2: v.selectedOptions[1] ? v.selectedOptions[1].value : null,
          available: v.availableForSale,
          price: Math.round(parseFloat(v.price.amount) * 100),
          compare_at_price: v.compareAtPrice
            ? Math.round(parseFloat(v.compareAtPrice.amount) * 100)
            : null
        };
      })
    };
  },

  hasTag: function(tags, tag) {
    if (!tag || !Array.isArray(tags)) return false;
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).toLowerCase() === String(tag).toLowerCase()) return true;
    }
    return false;
  },

  // Values with no whitespace go unquoted, which is the form proven to work on this store
  // for hyphenated tags (tag:color-sorting).
  tagTerm: function(tag) {
    return /\s/.test(tag) ? 'tag:"' + tag.replace(/"/g, '\\"') + '"' : 'tag:' + tag;
  },

  excludeTags: function() {
    var raw = window.oaLogoVersionsExcludeTags;
    if (!Array.isArray(raw)) raw = raw ? String(raw).split(',') : [];
    // oaComingSoonHideTag is only defined when the setting is non-blank, and its products
    // are served with the size picker and add-to-cart suppressed — nothing that can be
    // offered as an alternative.
    if (typeof window.oaComingSoonHideTag === 'string' && window.oaComingSoonHideTag) {
      raw = raw.concat(window.oaComingSoonHideTag.split(','));
    }
    return raw.map(function(t) { return String(t).trim(); }).filter(Boolean);
  },

  // Applied to results as well as to the query: the handle fallback and the per-colour
  // fetch are exact lookups, so no query term covers them.
  isExcluded: function(tags) {
    var excluded = this.excludeTags();
    if (!excluded.length || !Array.isArray(tags)) return false;
    for (var i = 0; i < excluded.length; i++) {
      if (this.hasTag(tags, excluded[i])) return true;
    }
    return false;
  },

  // handle -> { partner, role }, both directions, built once from OA_LOGO_VERSION_PAIRS.
  // Cached on the object; null until first use.
  _pairIndex: null,

  pairIndex: function() {
    if (this._pairIndex) return this._pairIndex;
    var index = {};
    var pairs = window.OA_LOGO_VERSION_PAIRS;

    if (pairs && typeof pairs === 'object') {
      Object.keys(pairs).forEach(function(original) {
        var updated = pairs[original];
        var a = String(original || '').trim().toLowerCase();
        var b = String(updated || '').trim().toLowerCase();
        // A blank or self-referential row is a typo, not a pair. Skipping it keeps the rest
        // of the map working rather than taking the feature down with it.
        if (!a || !b || a === b) return;
        index[a] = { partner: b, role: 'original' };
        index[b] = { partner: a, role: 'updated' };
      });
    }

    this._pairIndex = index;
    return index;
  },

  // The hand-maintained pairing for a handle, or null when it is not listed.
  mappedPair: function(handle) {
    if (!handle) return null;
    return this.pairIndex()[String(handle).trim().toLowerCase()] || null;
  },

  // The candidate handles for a sibling: the map first, then the "-2" suffix convention.
  siblingHandles: function(handle) {
    var mapped = this.mappedPair(handle);
    if (mapped) return [mapped.partner];
    if (/-2$/.test(handle)) return [handle.replace(/-2$/, '')];
    return [handle + '-2'];
  },

  /**
   * Which of the two is the original logo. The title marker wins where there is one: it
   * states the answer, while createdAt only infers it and would get the pair backwards if
   * a new colour were ever added to the old branding after the new branding shipped.
   * @private
   */
  assignRoles: function(current, alternative) {
    var currentParsed = this.parseTitle(current.title);
    var altParsed = this.parseTitle(alternative.title);

    if (currentParsed.marker && !altParsed.marker) {
      return currentParsed.marker;
    }
    if (altParsed.marker && !currentParsed.marker) {
      return altParsed.marker === 'updated' ? 'original' : 'updated';
    }
    if (currentParsed.marker && altParsed.marker && currentParsed.marker !== altParsed.marker) {
      return currentParsed.marker;
    }

    var currentAt = Date.parse(current.createdAt);
    var altAt = Date.parse(alternative.createdAt);
    // Without a usable date on both and no marker there is no way to say which logo is
    // which, and labelling them the wrong way round is worse than not offering the switch.
    if (isNaN(currentAt) || isNaN(altAt) || currentAt === altAt) return null;
    return currentAt < altAt ? 'original' : 'updated';
  },

  /**
   * The original path: search by title, then pull details for the two that survive.
   * @private
   */
  resolveFromSearch: function(handle, searchQuery, mine, tag) {
    var self = this;
    return OAStorefrontApi.fetch(this.searchQuery, { searchQuery: searchQuery })
      .then(function(data) {
        var nodes = data && data.products && data.products.nodes ? data.products.nodes : [];
        var ids = self.pickCandidateIds(nodes, handle, mine, tag);
        if (!ids) return self.byHandleFallback(handle, tag);

        return OAStorefrontApi.fetch(self.detailsQuery(), { ids: [ids.currentId, ids.alternativeId] })
          .then(function(details) {
            var list = details && details.nodes ? details.nodes : [];
            var pair = self.buildPair(list, handle, tag, false, mine);
            if (pair) pair.colours = ids.colours || [];
            return pair;
          })
          .then(function(pair) {
            return pair || self.byHandleFallback(handle, tag);
          });
      });
  },

  /**
   * The hand-mapped path: two exact handle lookups for the pair, and the title search
   * alongside purely for the colour family. Run in parallel — the colour list is a nicety
   * and must not delay or be able to fail the pairing.
   * @private
   */
  resolveFromMap: function(handle, mapped, searchQuery, mine) {
    var self = this;
    var tag = window.oaLogoVersionsTag;

    var pairFetch = OAStorefrontApi.fetch(this.buildHandleQuery([handle, mapped.partner]), {})
      .then(function(byHandle) {
        if (!byHandle) return null;
        var list = Object.keys(byHandle).map(function(k) { return byHandle[k]; });
        // acceptAnySibling and skipTagCheck: we asked for these two by name, and the map is
        // the assertion that they belong together.
        return self.buildPair(list, handle, tag, true, null, true);
      })
      .catch(function() { return null; });

    var coloursFetch = OAStorefrontApi.fetch(this.searchQuery, { searchQuery: searchQuery })
      .then(function(data) {
        return data && data.products && data.products.nodes ? data.products.nodes : [];
      })
      .catch(function() { return []; });

    return Promise.all([pairFetch, coloursFetch]).then(function(results) {
      var pair = results[0];
      if (!pair) {
        // A handle in the map that the catalogue does not have. Silent on the storefront,
        // but a reviewer running a preview should see why nothing rendered.
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[logo-versions] mapped pair did not resolve:', handle, '->', mapped.partner);
        }
        return null;
      }
      pair.colours = self.siblingColours(
        results[1], handle, pair.alternative.handle, self.parseTitle(pair.alternative.title)
      );
      return pair;
    });
  },

  /**
   * Resolve the two branding versions of the product on screen.
   *
   * @param {string} handle - the handle currently on the PDP
   * @param {string} title - its full title, colour half included
   * @returns {Promise<Object|null>} { current, alternative, currentRole, alternativeRole }
   *   where role is 'original' or 'updated'. Resolves null when there is no sibling, and
   *   never rejects — a failed lookup means the test simply does not render.
   */
  findAlternative: function(handle, title) {
    var self = this;
    if (!handle || !title || !window.OAStorefrontApi) return Promise.resolve(null);

    var cached = this.cacheGet(handle);
    if (cached !== undefined) return Promise.resolve(cached);
    if (this.inflight[handle]) return this.inflight[handle];

    var tag = window.oaLogoVersionsTag;
    if (!tag) return Promise.resolve(null);

    var mine = this.parseTitle(title);
    if (!mine.base) return Promise.resolve(null);

    var mapped = this.mappedPair(handle);

    // Search on the MARKER-STRIPPED base, so the query finds the sibling whichever side we
    // are standing on: from "… Tank Top New Branding" the prefix is still "… Tank Top",
    // which matches both. Searching the raw title would only ever find our own version.
    var prefix = mine.rawBase;
    this.UPDATED_MARKERS.concat(this.ORIGINAL_MARKERS).forEach(function(phrase) {
      prefix = prefix.replace(new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig'), ' ');
    });
    prefix = prefix.replace(/\s+/g, ' ').trim().replace(/"/g, '\\"');
    if (!prefix) return Promise.resolve(null);

    // Title only, no tag term. multiple-logo-versions marks a product that HAS a
    // counterpart, so colours that exist in one branding version only are untagged — and
    // ANDing the tag here returned a colour list of exactly one. The tag is still required
    // to call two products a pair, in pickCandidateIds; it just no longer decides which
    // colours the alternative is offered in.
    var searchQuery = 'title:"' + prefix + '*"';
    this.excludeTags().forEach(function(tag) {
      searchQuery += ' AND NOT ' + self.tagTerm(tag);
    });

    // Listed by hand wins: the pair comes from the handles alone, and the title search is
    // demoted to building the colour family, so a failed or empty one costs the colour
    // selector rather than the pairing.
    var request = (mapped
      ? this.resolveFromMap(handle, mapped, searchQuery, mine)
      : this.resolveFromSearch(handle, searchQuery, mine, tag)
    ).then(function(result) {
      self.cacheSet(handle, result);
      // Cache the mirror image too: switching to the sibling and back is one round trip,
      // not two, and the pair is symmetric by construction.
      if (result) {
        self.cacheSet(result.alternative.handle, {
          current: result.alternative,
          alternative: result.current,
          currentRole: result.alternativeRole,
          alternativeRole: result.currentRole
        });
      }
      return result;
    }).catch(function() {
      return null;
    });

    // Store a non-rejecting copy so a second caller waiting on this request cannot inherit
    // its failure; every path here already falls back to null.
    var tracked = request.catch(function() { return null; });
    this.inflight[handle] = tracked;
    tracked.then(function() {
      if (self.inflight[handle] === tracked) delete self.inflight[handle];
    });

    return request;
  },

  /**
   * Narrow the search hits to the two products worth fetching in full: ours, and the one
   * sibling that is the same garment in the same colour.
   * @private
   */
  pickCandidateIds: function(nodes, handle, mine, tag) {
    var self = this;
    var currentId = null;
    var alternativeId = null;
    var alternativeHandle = null;
    var alternativeParsed = null;
    var all = [];

    nodes.forEach(function(node) {
      if (!node || self.isExcluded(node.tags)) return;
      all.push(node);
      // Pairing still demands the tag on both sides — that is what the tag is for.
      if (!self.hasTag(node.tags, tag)) return;
      if (node.handle === handle) {
        currentId = node.id;
        return;
      }
      if (alternativeId) return;
      if (self.isPair(mine, self.parseTitle(node.title))) {
        alternativeId = node.id;
        alternativeHandle = node.handle;
        alternativeParsed = self.parseTitle(node.title);
      }
    });

    if (!currentId || !alternativeId) return null;
    return {
      currentId: currentId,
      alternativeId: alternativeId,
      // Every OTHER colour of the branding version we are pointing at. Ids and titles only:
      // the search is deliberately light, and fetching variants for a 20-colour family would
      // put the document over the complexity budget. Details are pulled per colour on demand.
      colours: self.siblingColours(all, handle, alternativeHandle, alternativeParsed)
    };
  },

  /**
   * Full record for one colour the search already identified, fetched on demand.
   *
   * The colour list from findAlternative carries ids and titles only. Pulling variants for
   * a 20-colour family up front would blow the complexity budget for a control most
   * shoppers never touch, so a colour is loaded when it is actually picked.
   *
   * @param {string} gid - product id from the `colours` list
   * @returns {Promise<Object|null>} transformed product, or null
   */
  fetchColour: function(gid) {
    var self = this;
    if (!gid || !window.OAStorefrontApi) return Promise.resolve(null);

    var key = 'colour:' + gid;
    var cached = this.cacheGet(key);
    if (cached !== undefined) return Promise.resolve(cached);
    if (this.inflight[key]) return this.inflight[key];

    var request = OAStorefrontApi.fetch(this.detailsQuery(), { ids: [gid] })
      .then(function(details) {
        var node = details && details.nodes ? details.nodes[0] : null;
        if (node && self.isExcluded(node.tags)) node = null;
        var product = self.transform(node);
        self.cacheSet(key, product);
        return product;
      })
      .catch(function() { return null; });

    var tracked = request.catch(function() { return null; });
    this.inflight[key] = tracked;
    tracked.then(function() {
      if (self.inflight[key] === tracked) delete self.inflight[key];
    });

    return request;
  },

  /**
   * The colour family of one branding version, from the light search hits.
   *
   * A candidate belongs to the same version when its title marker matches — every colour of
   * "… New Branding | X" parses as 'updated', every colour of "… | X" as null. Where neither
   * side carries a marker (the "-2" naming), the handle suffix stands in for it.
   * @private
   */
  siblingColours: function(nodes, currentHandle, altHandle, altParsed) {
    var self = this;
    if (!altParsed || !altHandle) return [];
    // When neither title carries a marker the two versions are identical strings, so the
    // marker test below cannot separate them and the handle suffix has to.
    var markerless = altParsed.marker === null;
    var altSuffixed = /-2$/.test(altHandle);
    var altMapped = this.mappedPair(altHandle);

    // Both versions titled identically AND not named by the "-2" convention: nothing in the
    // title or the handle says which logo version a colour belongs to, and the tests below
    // would let both families through — offering the same colour twice, one of them
    // switching the shopper back to the version they are already on. The map is the only
    // thing that knows, so where it answers, it decides.
    var ambiguous = markerless && !altSuffixed && !!altMapped;
    var out = [];

    nodes.forEach(function(node) {
      if (node.handle === currentHandle) return;
      var parsed = self.parseTitle(node.title);
      if (parsed.base !== altParsed.base) return;

      if (ambiguous) {
        // An unmapped colour cannot be placed in either family here, so it is left out
        // rather than guessed at. Add it to the map to have it offered.
        var mapped = self.mappedPair(node.handle);
        if (!mapped || mapped.role !== altMapped.role) return;
      } else {
        if (parsed.marker !== altParsed.marker) return;
        if (markerless && /-2$/.test(node.handle) !== altSuffixed) return;
      }

      if (!parsed.colour) return;
      out.push({
        id: node.id,
        handle: node.handle,
        title: node.title,
        colour: parsed.colour
      });
    });

    return out;
  },

  /**
   * Last resort for pairs named by the "-2" convention rather than paired by title.
   * @private
   */
  byHandleFallback: function(handle, tag) {
    var self = this;
    var candidates = this.siblingHandles(handle);
    return OAStorefrontApi.fetch(this.buildHandleQuery([handle].concat(candidates)), {})
      .then(function(byHandle) {
        if (!byHandle) return null;
        var list = Object.keys(byHandle).map(function(k) { return byHandle[k]; });
        // Titles are not re-checked here: we asked for these handles by name, so whatever
        // comes back that is not the current product IS the sibling. Re-testing the title
        // would fail for exactly the pairs this fallback exists to cover.
        return self.buildPair(list, handle, tag, true, null);
      })
      .catch(function() { return null; });
  },

  /**
   * Turn a list of full product records into the resolved pair.
   * @private
   */
  buildPair: function(nodes, handle, tag, acceptAnySibling, mine, skipTagCheck) {
    var self = this;
    var current = null;
    var alternative = null;

    nodes.forEach(function(node) {
      // isExcluded stays, always: the map asserts two products are the same garment, not
      // that either is sellable, so preview-launch and coming-soon are still filtered.
      if (!node || self.isExcluded(node.tags)) return;
      var product = self.transform(node);
      if (!product) return;
      // Listing a pair by hand IS the assertion that they are a pair, so the tag is not
      // demanded of them — the map exists for products that are not tagged.
      if (!skipTagCheck && !self.hasTag(product.tags, tag)) return;
      if (product.handle === handle) {
        current = product;
      } else if (!alternative) {
        if (acceptAnySibling || (mine && self.isPair(mine, self.parseTitle(product.title)))) {
          alternative = product;
        }
      }
    });

    if (!current || !alternative) return null;

    // The map states the direction; assignRoles only infers it from a title marker or
    // created_at, and would get the pair backwards if a colour were added to the old
    // branding after the new one shipped.
    var mapped = this.mappedPair(current.handle);
    var currentRole = mapped ? mapped.role : this.assignRoles(current, alternative);
    if (!currentRole) return null;

    return {
      current: current,
      alternative: alternative,
      currentRole: currentRole,
      alternativeRole: currentRole === 'original' ? 'updated' : 'original'
    };
  }
};
